/**
 * Human-readable summary for Apply copied → sent editor preview.
 */

import type { BulkUpdatePreparedDropsPayload } from '@/hooks';
import { filterSenderNamesMax11 } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

const MAX_TEXT_PREVIEW = 3;

export function campaignMismatch(
  payload: BulkUpdatePreparedDropsPayload,
  workspaceWiserId: number,
): boolean {
  if (payload.wiserId == null) return false;
  return payload.wiserId !== workspaceWiserId;
}

export interface ApplyCopiedPreviewLines {
  lines: string[];
  canConfirm: boolean;
  mismatchMessage: string | null;
}

export function buildApplyCopiedPreviewSummary(
  payload: BulkUpdatePreparedDropsPayload,
  workspaceWiserId: number,
  campaigns: Array<{ id: number; name: string }>,
  gateways: Array<{ id: number; name: string }>,
): ApplyCopiedPreviewLines {
  const mismatch = campaignMismatch(payload, workspaceWiserId);
  const lines: string[] = [];

  if (payload.wiserId != null && payload.wiserId > 0) {
    const name = campaigns.find((c) => c.id === payload.wiserId)?.name;
    lines.push(`Campaign: ${name ?? `ID ${payload.wiserId}`}`);
  }
  if (payload.routeId != null && payload.routeId > 0) {
    const gw = gateways.find((g) => g.id === payload.routeId)?.name;
    lines.push(`Gateway: ${gw ?? `ID ${payload.routeId}`}`);
  }
  if (payload.senderNames?.length) {
    const s = filterSenderNamesMax11(payload.senderNames);
    lines.push(`Senders (${s.length}): ${s.join(', ')}`);
  }
  if (payload.smsTexts?.length) {
    const t = payload.smsTexts.slice(0, MAX_TEXT_PREVIEW);
    const more = payload.smsTexts.length - t.length;
    lines.push(
      `Texts (${payload.smsTexts.length}): ${t.map((x) => `"${x.slice(0, 48)}${x.length > 48 ? '…' : ''}"`).join(' · ')}${more > 0 ? ` … +${more} more` : ''}`,
    );
  }

  const hasMergeFields =
    (payload.routeId != null && payload.routeId > 0) ||
    (payload.senderNames?.length ?? 0) > 0 ||
    (payload.smsTexts?.length ?? 0) > 0;

  if (lines.length === 0) {
    lines.push('No fields to apply (copy config from a stats drop first).');
  }

  return {
    lines,
    canConfirm: !mismatch && hasMergeFields,
    mismatchMessage: mismatch
      ? 'Copied campaign does not match this workspace. Switch campaign or copy from a drop in this campaign.'
      : null,
  };
}
